import React, { useState } from 'react'
import FlexContainer from '@components/common/FlexContainer'
import { AuthorizedInteractable } from '@components/common/auth'
import { ChefConcept, ChefSearchMenuItem, ChefScheduledOrder } from '@types'
import { XSpacing, YSpacing } from '@components/common'
import AddToExistingMenuCard from '@components/chef/chefSections/AddToExistingMenuCard'
import services from '@services'
import presenters from '@presenters'

interface StageMenuItemsProps {
  onUnstageMenuItem: (item: ChefSearchMenuItem) => void

  stagedMenuItems: ChefSearchMenuItem[]

  calcMargin: (item: ChefSearchMenuItem) => number
  chefId: string
  asyncLoadConcept: (chefId: string) => Promise<void>
  saveMenuConcept: (data: any) => Promise<void>
  loadChefOrders: (req: {
    chefId: string
    calendarStartDate: string
    calendarEndDate: string
    withoutItems: boolean
  }) => ChefScheduledOrder[]
  loadConcepts: () => Promise<void>
  menuConcepts: ChefConcept[]
}

const StageMenuItems: React.FC<StageMenuItemsProps> = ({
  stagedMenuItems,
  onUnstageMenuItem,
  calcMargin,
  chefId,
  asyncLoadConcept,
  saveMenuConcept,
  loadChefOrders,
  loadConcepts,
  menuConcepts,
}) => {
  const [showAddToExistingMenuCard, setShowAddToExistingMenuCard] =
    useState(false)
  const [collapseTableToggle, setCollapseTableToggle] = useState(false)

  const { ChefService, UIService } = services
  const { pResponseConcept } = presenters.Api

  const removeItem = (item: ChefSearchMenuItem) => {
    onUnstageMenuItem(item)
  }

  const removeAllItems = () => {
    stagedMenuItems.forEach(onUnstageMenuItem)
  }

  const toggleCollapseTable = () => {
    setCollapseTableToggle((prev) => !prev)
  }

  const openAddToExistingMenuCard = () => {
    setShowAddToExistingMenuCard(true)
  }

  const closeAddToExistingMenuCard = () => {
    setShowAddToExistingMenuCard(false)
  }

  const newEditMenuCard = () => {
    const items = {
      chefId,
      conceptsMenuItems: stagedMenuItems?.map((item) => {
        return {
          menuItem: { ...item, cost: item.chefPrice, price: item.marketPrice },
        }
      }),
    }
    const concept = pResponseConcept(items)
    ChefService.setEditConcept(concept)
    UIService.EditMenuConcept.show()
  }

  return (
    <>
      {stagedMenuItems.length > 0 && (
        <div className="bg-white rounded-lg border overflow-hidden">
          <div className="px-6 py-4 border-b bg-gray-50">
            <h2
              className="text-lg font-bold text-gray-900 hover:text-blue-700 cursor-pointer"
              onClick={toggleCollapseTable}
            >
              Staged Menu Items ({stagedMenuItems.length})
            </h2>
          </div>

          <div className="overflow-x-auto">
            <table
              className={`w-full text-sm ${
                collapseTableToggle ? 'collapse' : ''
              }`}
            >
              <thead className="bg-gray-100 border-b">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700 text-xs uppercase">
                    ITEM NAME
                  </th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700 text-xs uppercase">
                    MARKET TYPE
                  </th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700 text-xs uppercase">
                    DISH TYPE
                  </th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700 text-xs uppercase">
                    GO MEAL TYPE
                  </th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700 text-xs uppercase">
                    CUISINE TYPES
                  </th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700 text-xs uppercase">
                    CHEF PRICE
                  </th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700 text-xs uppercase">
                    MARKET PRICE
                  </th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700 text-xs uppercase">
                    MARGIN %
                  </th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700 text-xs uppercase">
                    ACTION
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y">
                {stagedMenuItems.map((item, i) => (
                  <tr
                    key={item.id}
                    className={`group relative ${
                      i % 2 === 0 ? 'bg-green-50' : 'bg-green-200'
                    } hover:bg-green-300`}
                  >
                    <td className="px-4 py-3 font-medium text-gray-900">
                      {item.name}
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {item.marketType}
                    </td>
                    <td className="px-4 py-3 text-gray-700">{item.mealType}</td>
                    <td className="px-4 py-3">
                      {item.tagsGroupOrderMealTypeList.length > 0 && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {item.tagsGroupOrderMealTypeList}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {item.tagsCuisineList.length > 0 && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          {item.tagsCuisineList}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {item.chefPrice}
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {item.marketPrice}
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {calcMargin(item).toFixed(2)}%
                    </td>
                    <td className="px-4 py-3">
                      <button
                        className="btn bg-gray-700 text-white hover:text-white hover:bg-gray-800 whitespace-nowrap"
                        onClick={() => removeItem(item)}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <YSpacing height="35px" />

            <AuthorizedInteractable roles={['master admin', 'chef lead']}>
              <FlexContainer
                flexDirection="row"
                justifyContent="flex-start"
                alignItems="flex-start"
                padding="0 0 15px 15px"
              >
                <button
                  className="btn bg-blue-600 hover:bg-blue-700 text-white hover:text-white"
                  onClick={toggleCollapseTable}
                >
                  {collapseTableToggle ? 'Expand' : 'Collapse'} Items
                </button>
                <XSpacing width="15px" />
                <button
                  className="btn bg-blue-600 hover:bg-blue-700 text-white hover:text-white"
                  onClick={newEditMenuCard}
                >
                  Create New Menu Card
                </button>
                <XSpacing width="15px" />
                <button
                  className="btn bg-green-600 hover:bg-green-700 text-white hover:text-white"
                  onClick={openAddToExistingMenuCard}
                >
                  Add to Existing Menu Card
                </button>
                <XSpacing width="15px" />
                <button
                  onClick={removeAllItems}
                  className="btn bg-gray-700 text-white hover:text-white hover:bg-gray-800 border-gray-700 whitespace-nowrap"
                >
                  Remove All Items
                </button>
              </FlexContainer>
            </AuthorizedInteractable>
          </div>

          <AddToExistingMenuCard
            chefId={chefId}
            showAddToExistingMenuCard={showAddToExistingMenuCard}
            stagedMenuItems={stagedMenuItems}
            onClose={closeAddToExistingMenuCard}
            menuConcepts={menuConcepts}
            calcMargin={calcMargin}
            asyncLoadConcept={asyncLoadConcept}
            saveMenuConcept={saveMenuConcept}
            loadChefOrders={loadChefOrders}
            loadConcepts={loadConcepts}
          />
        </div>
      )}
    </>
  )
}

export default StageMenuItems
